import { liveQuery } from 'dexie';
import { db, META, type LocalHousehold } from '@/db/db';
import { getCloudClient } from '@/auth/client';
import { returnToLocalHousehold } from '@/auth/account';
import { SyncEngine } from './engine';
import { ScopeChanged, type Scope, type SyncTransport } from './protocol';

async function rpc(userId: string, name: string, args: Record<string, unknown>) {
  const session = await getCloudClient().auth.getSession();
  if (session.error) throw session.error;
  if (session.data.session?.user.id !== userId) throw new ScopeChanged();
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<never>((_, reject) => { timer = setTimeout(() => { controller.abort(); reject(new Error('Sync request timed out.')); }, 20_000); });
    const response = await Promise.race([getCloudClient().rpc(name, args).abortSignal(controller.signal), timeout]);
    if (response.error) throw Object.assign(new Error(response.error.message), { code: response.error.code });
    return response.data;
  } finally { clearTimeout(timer); }
}
export const cloudTransport = (userId: string): SyncTransport => ({
  apply: (householdId, operations) => rpc(userId, 'apply_operations', { household_id: householdId, operations }),
  pull: (householdId, cursor) => rpc(userId, 'pull_changes', { household_id: householdId, after_seq: cursor, page_size: 200 }),
});
/** One runtime per provider; IndexedDB lease also coordinates other tabs. */
export function startSync() {
  let stopped = false; let running = false; let requested = false; let failures = 0; let nextAttempt = 0;
  let scope: Scope | null = null;
  let channel: ReturnType<ReturnType<typeof getCloudClient>['channel']> | undefined;
  const wake = () => {
    requested = true;
    if (stopped || running || !navigator.onLine || Date.now() < nextAttempt || !scope) return;
    const selected = scope;
    running = true; requested = false;
    void new SyncEngine(cloudTransport(selected.userId)).cycle(selected).then(more => { failures = 0; nextAttempt = 0; if (more) requested = true; }).catch(async error => {
      if (error instanceof ScopeChanged) return;
      failures++;
      nextAttempt = Date.now() + Math.min(300_000, 1000 * 2 ** Math.min(failures, 8)) * (0.5 + Math.random());
      if (['42501', 'PGRST301'].includes(error?.code) && !stopped && scope === selected) await returnToLocalHousehold();
    }).finally(() => { running = false; if (requested && !stopped) setTimeout(wake, 100); });
  };
  const sub = liveQuery(async () => {
    const [rows, ops] = await Promise.all([db.meta.bulkGet([META.household, META.session, META.authGeneration, 'privacyBusy']), db.outbox.toArray()]);
    return { rows, queue: ops.map(o => `${o.operation_id}:${o.status}:${o.client_ts}:${o.next_attempt_at}`).join('|') };
  }).subscribe(({ rows }) => {
    const h = rows[0]?.value as LocalHousehold | undefined;
    const session = rows[1]?.value as { userId?: string } | undefined;
    const next = !rows[3] && h?.cloud && h.accountId === session?.userId ? { householdId: h.id, userId: h.accountId!, generation: (rows[2]?.value ?? 0) as number } : null;
    if (JSON.stringify(next) !== JSON.stringify(scope)) {
      scope = next; failures = 0; nextAttempt = 0;
      if (channel) void getCloudClient().removeChannel(channel);
      channel = next && typeof getCloudClient().channel === 'function' ? getCloudClient().channel(`household-sync:${next.householdId}`).on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'change_log', filter: `household_id=eq.${next.householdId}` }, wake).subscribe() : undefined;
    }
    wake();
  });
  const foreground = () => { if (navigator.onLine) { nextAttempt = 0; wake(); } };
  const timer = setInterval(wake, 15_000);
  window.addEventListener('online', foreground); window.addEventListener('focus', foreground);
  document.addEventListener('visibilitychange', foreground); window.addEventListener('kai-sync-now', foreground);
  return () => { stopped = true; sub.unsubscribe(); clearInterval(timer); if (channel) void getCloudClient().removeChannel(channel);
    window.removeEventListener('online', foreground); window.removeEventListener('focus', foreground);
    document.removeEventListener('visibilitychange', foreground); window.removeEventListener('kai-sync-now', foreground); };
}
